import BookingTargetRadio from '@/modules/receipt/review/booking-target-radio'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { makeStyles } from '@mui/styles'
import FineUploaderTraditional from 'fine-uploader-wrappers'
import { omitBy, isNull } from 'lodash'
import clsx from 'clsx'
import { alpha } from '@mui/material/styles'

import lang from '@/lang'
import config from '@/config'
import paymentConfig from '@/modules/payment/config'
import documentConfig from '@/modules/document/config'
import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import {
  USER_RIGHTS,
  RECEIPT_TYPES,
  RECEIPT_PAYMENT_TYPES,
  SUBSCRIPTION_MODULES,
  SubscriptionTypeTypes,
  SystemFolders,
  errorCodes,
  experimentalFeatures,
  payKinds,
  priceKinds,
} from '@/modules/base/util/constants'
import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'
import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import { shouldHideBookingSectionsInReview } from '@/modules/receipt/helpers/invoice-review-config'
import Loader from '@/modules/base/components/loader/loader'
import { pollTransaction } from 'redux/ducks/banking/_helpers'
import { clearFiles, setAndValidateData, assignFile, assignFiles } from '@/redux/ducks/receipt/edit'
import ExpenseService from '@/modules/expense/ExpenseService'
import { BookingService } from '@/modules/banking/services'
import SupplierService from '@/modules/supplier/supplierService'
import { prepareOcrFillData } from '@/modules/receipt/_helpers/prepareOcrFillData'
import openVatPromptModal from '@/modules/_shared/modals/vat-prompt-modal.opener'
// FLAG:useDocuAi — the same pure reader the form already uses for the „nicht buchbar" notice; it
// returns null flag-OFF, so the save path below is byte-identical without the flag.
import { findUnbookableVatFact } from '@/modules/receipt/_helpers/unbookableVatFact'
import { doDisplayModalBeforeSave } from '@/modules/_helpers/vatValidation'
import ExpenseModel from '@/modules/expense/model/expense_model'
import RevenueModel from '@/modules/revenue/models/revenue_model'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { BASE_DATA_TYPES } from '@/modules/base/components/generics/constants'
import { generateProcessId } from '@/modules/banking/_helpers/generateProcessId'
import handleUploadError from '@/modules/file/_helpers/handleUploadError'
import validateFileName from '@/utils/validateFileName'
import { ACCOUNT_CATEGORY } from '@/modules/economicAsset/constants'
import CreateEconomicAssetModal from '@/modules/expense/components/list/create-economic-asset-modal/create-economic-asset-modal'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import { ackUiEvent } from '@/modules/document/utils/ui-event-ack'
import ReceiptPreviewer from '@/modules/receipt/preview/receipt-previewer'
import { useSelectedReceiptReview } from '@/modules/receipt/review/use-selected-receipt-review'
import { saveDocumentInvoiceReview as saveDocumentInvoiceReviewRequest } from '@/modules/receipt/review/save-document-invoice-review'
import { flipReviewBookingAmounts, isReviewLedgerFlip } from '@/modules/receipt/review/review-booking-ledger' // FLAG:useDocuAi
import { resolveAppliedOutstandingAmount } from '@/modules/receipt/form/resolve-applied-outstanding-amount'
import ThemeInterface from '@/theme-interface'
import receiptConfig from '@/modules/receipt/config'
import RevenueService from '@/modules/revenue/revenueService'
import ReceiptEditFormWrapper from '@/modules/receipt/receipt-edit-form-wrapper'
import store, { useAppDispatch, useAppSelector } from '@/redux/store'
import { DocumentAnalysisService } from '../document/services/documentAnalysis'
import { modalConfirmation } from '@/modules/_helpers/openModal'
import { AccountInfo, Booking, FileToAssign, JsonMap, ReceiptData } from '@/modules/receipt/types'
// FLAG:useDocuAi — central invoice-review mode plumbing (inert unless documentReviewFileId is passed).
import FileService from '@/modules/file/fileService'
import { Checkbox } from '@/modules/base/components/inputs'
// FLAG:useDocuAi — DMS→DocuAI upload for the New Expense/Revenue form (gated; flag-OFF = legacy FineUploader).
import { isDmsReceiptUploadEnabled } from '@/redux/ducks/document/uploadHelpers'
// FLAG:useDocuAi — THE receipt upload, shared with the expense/revenue list dropzone.
import { getReceiptCreateUploadAction, RECEIPT_DMS_UPLOAD_ACTIONS } from '@/modules/receipt/helpers/dms-receipt-upload'
import { useReceiptDmsUpload } from '@/modules/receipt/helpers/use-receipt-dms-upload'
// FLAG:useDocuAi — T23: link a file uploaded INTO this form to the open receipt right away.
import { attachUploadedReceipt } from '@/modules/receipt/helpers/attach-uploaded-receipt'
// FLAG:useDocuAi — shared accept/refuse rule for receipt uploads (same helper as the list dropzone).
import {
  getUnsupportedReceiptFilesMessage,
  partitionReceiptUploadFiles,
} from '@/modules/receipt/helpers/receipt-upload-file-types'
import {
  buildReviewPayeePersistence,
  PartyResolution,
  validateReviewPayeeForSave,
} from '@/modules/receipt/review/review-payee-save'
// FLAG:useDocuAi — fields that the reviewer must be able to clear (see there).
import { resolveClearableReviewValue } from '@/modules/document/components/review/reviewed-field-values'
// FLAG:useDocuAi — 'expense' | 'revenue', the same two values as RECEIPT_TYPES and the column
// documentInvoiceDetails.bookingTarget.
import { BookingTarget } from '@/modules/receipt/review/review-target'
// FLAG:useDocuAi — the KoSIT/format verdict for the e-invoice, the same answer that drives the red
// tile badge (INVOICE_ANALYSIS_STATE.INVALID).
import { hasInvalidValidationResult } from '@/modules/document/utils/invoice-analysis-state'
import { awaitableConfirmationModal } from '@/modules/base/components/modal/_helpers/awaitableConfirmationModal'

const { NOT_FOUND, INVALID, MAX_LENGTH_EXCEEDED } = errorCodes
const { CASH, OFFLINE } = payKinds

const ALLOWED_FILE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'pdf', 'xml', 'ZUGFeRD']
const MAX_FILESIZE_BYTES = 25000000
const MAX_IMAGE_HEIGHT = 3000
const MAX_IMAGE_WIDTH = 3000
const MAX_FILES_PER_UPLOAD = 25
const allowedExtensionsStrings = ALLOWED_FILE_EXTENSIONS.map(ext => `'${ext}'`).join(', ')
const NO_FILES_TO_UPLOAD_ERROR = 'No files to upload.'

// FLAG:useDocuAi — used only by the invoice-review helpers below.
const isPlainObject = value => !!value && typeof value === 'object' && !Array.isArray(value)

const getMappedReceipts = (receipts) => {
  if (!receipts || receipts.length <= 0) return []
  const { receiptBaseUrl } = receiptConfig

  return receipts.map(receipt => ({
    ...receipt,
    uploaded: true,
    url: (!receipt.url.startsWith('data:'))
      ? `${receiptBaseUrl}${receipt.url}`
      : receipt.url,
  }))
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    alignItems: 'flex-start',
    position: 'relative',
    height: '100%',
    background: theme.white,
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 0 0',
    width: '100%',
  },
  loader: {
    height: '100%',
  },
  // eslint-disable-next-line
  block: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: `calc(100vh - ${theme.pageContentOffset}px)`,
  },
  form: {
    composes: ['$block'],
    borderLeft: `2px solid ${theme.borderColor}`,
    padding: 30,
    marginBottom: 40,
    maxHeight: `calc(100vh - ${theme.pageContentOffset}px)`,
    minWidth: 200,
    maxWidth: 660,
    width: '50%',
    overflow: 'auto',
    background: theme.backgroundColor,
  },
  // FLAG:useDocuAi — drawer-embedded review renders the form column alone, full width;
  // the drawer already shows the document preview on its left side. Heights follow the
  // host's scroll container instead of the page layout.
  formOnly: {
    width: '100%',
    maxWidth: 'none',
    minWidth: 0,
    minHeight: 0,
    maxHeight: 'none',
    marginBottom: 0,
    borderLeft: 0,
  },
  // FLAG:useDocuAi — invoice-review header (kind label + "Ausgabe erstellen" checkbox).
  reviewActions: {
    paddingBottom: 20,
    marginBottom: 20,
    borderBottom: theme.border,
  },
  reviewKind: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  reviewKindCaption: {
    color: theme.textMuted,
    fontSize: theme.fontSizes.default,
    marginRight: 8,
  },
  reviewKindValue: {
    color: theme.black,
    fontSize: theme.fontSizes.default,
    fontWeight: theme.fontWeights.bold,
  },
  reviewActionLabel: {
    color: theme.black,
    fontSize: theme.fontSizes.default,
    marginLeft: 10,
  },
  // FLAG:useDocuAi — rationale for a preselected booking decision. Indented to the check-box label
  // (check-box visual + its marginLeft) so it reads as a note ABOUT IT rather than a separate
  // statement.
  reviewActionHint: {
    color: theme.textMuted,
    fontSize: theme.fontSizes.small,
    marginTop: 4,
    marginLeft: 30,
  },
  upload: {
    composes: ['$block'],
    background: theme.backgroundColor,
    maxHeight: `calc(100vh - ${theme.pageContentOffset}px)`,
    flex: 1,
    minWidth: 200,
    overflow: 'hidden',
    position: 'relative',
  },
  analyzeLoader: {
    height: '100%',
    background: 'rgba(255, 255, 255, 0.5)',
    '& > *': {
      padding: 20,
      backgroundColor: 'white',
      border: theme.border,
      borderRadius: 10,
    },
  },
  analyze: {
    position: 'absolute',
    height: '100%',
    width: '100%',
  },
  analyzeAnimation: {
    position: 'absolute',
    left: 0,
    top: 60,
    width: '100%',
    height: 20,
    backgroundColor: alpha(theme.primary, 0.5),
    zIndex: 1,
    animation: '$scanning 3s cubic-bezier(0.3, 0.6, 0.5, 0.1)',
    animationIterationCount: 'infinite',
  },
  // eslint-disable-next-line
  '@keyframes scanning': {
    '50%': {
      transform: 'translateY(calc(100vh - 255px))',
    },
  },
}))

interface ReceiptEditInterface {
  type: string
  data: ReceiptData
  accounts?: AccountInfo[]
  isAnalysingMode?: boolean
  isAllowedToOpenEconomicAssetModal?: boolean
  // FLAG:useDocuAi — invoice-review props; legacy expense/revenue callers never pass these.
  bookingFieldsReadOnly?: boolean
  documentReviewFileId?: string
  // FLAG:useDocuAi — turn on the positions review tools WITHOUT a document. Independent of
  // documentReviewFileId on purpose: an expense created without any receipt has no file to review,
  // but should still get the same line-item UI as the review component.
  positionReviewTools?: boolean
  /**
   * FLAG:docuAiEnhancements — additive slot, rendered at the END of the form column.
   * Without the prop, no line changes for any existing host.
   *
   * Why a slot INSIDE this component rather than a sibling next to it: the root of the
   * expense/revenue edit page is a fixed `calc(100vh - menuHeight)` box with `overflow: hidden`.
   * Anything rendered AFTER ReceiptEdit sits flush with the lower edge — visible only while the
   * window is exactly as tall as the layout assumes,
   * and without a scrollbar that could ever make
   * it reachable. The receipt chat failed this way twice: it was in the DOM and still invisible.
   *
   * The form column, by contrast, already scrolls on its own (`form`: `overflow: auto`). Anything
   * inserted here is therefore always reachable, regardless of window height and zoom.
   */
  formFooter?: React.ReactNode
  documentReviewIncludesPositions?: boolean
  documentReviewCreateExpense?: boolean
  showDocumentReviewCreateExpense?: boolean
  documentReviewKindLabel?: string
  documentReviewTags?: string[]
  onDocumentReviewCreateExpenseChange?: (value: boolean) => void
  // FLAG:useDocuAi — the reviewer's explicit booking decision (plan `review-booking-target`).
  // ONLY the two document review hosts pass it in — a receipt uploaded through expenses or revenues
  // already has its ledger. Without the props, no line changes for any other host: no check box in
  // the review header, and `bookingTarget` is absent from the stored documentInvoiceDetails (rather
  // than silently being changed to an expense there).
  showDocumentReviewBookingTarget?: boolean
  documentReviewBookingTarget?: BookingTarget
  /** Explicit accounting choice; document retains review data without creating a receipt. */
  onDocumentReviewBookingTargetChange?: (target: BookingTarget) => void
  /**
   * FLAG:useDocuAi — may the choice offer „Nur Dokument“? Default yes, which is what the two
   * Documents review hosts want. The review embedded in Ausgaben/Einnahmen passes false: there the
   * entry point already asserts a ledger (decision Ralf, 2026-09-16), and expense ↔ revenue stays
   * correctable. See BookingTargetRadio for the full rationale.
   */
  documentReviewAllowDocumentTarget?: boolean
  /**
   * Rationale for a PRESELECTION, directly below the check box. The host passes it only while the
   * suggestion still drives the check box (no manual click, no stored decision) — this component
   * only displays what it receives.
   */
  documentReviewBookingTargetHint?: string | null
  // FLAG:useDocuAi — "review again": this save re-opens an ALREADY reviewed invoice, so the
  // server must re-derive the (draft) expense even though no review is being completed. The
  // resolution is the user's answer from the confirmation dialog about a renamed payee.
  documentReviewIsReReview?: boolean
  documentReviewPartyResolution?: PartyResolution | null
  // FLAG:useDocuAi — the document's persisted documentInvoiceDetails, supplied by
  // every review entry point so the ONE shared paid-status/payment hint renders
  // identically everywhere (data in; the component owns how/when it shows).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  documentReviewDetails?: Record<string, any> | null
  // FLAG:useDocuAi — optional preview override (the invoice-artifact switcher on the review page);
  // legacy expense/revenue callers never pass it, so the normal receipt previewer renders unchanged.
  previewOverride?: React.ReactNode
  // FLAG:useDocuAi — drawer-embedded review: render ONLY the form column (no upload/preview column);
  // the document drawer already shows the preview on its left side.
  documentReviewFormOnly?: boolean
  onSaveAbort: () => void
  onSaveSuccess: () => void
  onSaveError: () => void
  // FLAG:useDocuAi — flag-ON receipt upload hands the created file id up so the parent can show
  // the embedded review once analysed (instead of the legacy inline OCR form).
  onDmsReceiptUploaded?: (fileId: string) => void
}

interface SaveRequest {
  priceTotal: number;
  description: string;
  receiptNumber: string;
  date: string | null;
  tags: string[];
  bookingAssignments: Booking[];
  fileIds: Array<{ id: string; metaData: JsonMap }>;
  customerId?: string;
  supplierId?: string;
  payee?: string;
  sender?: string;
  [key: string]: unknown;
}

const ReceiptEdit = forwardRef((props: ReceiptEditInterface, ref) => {
  const {
    type,
    data,
    isAnalysingMode = false,
    accounts = [],
    isAllowedToOpenEconomicAssetModal = false,
    // FLAG:useDocuAi — invoice-review props (defaults keep legacy behavior).
    bookingFieldsReadOnly = false,
    documentReviewFileId,
    positionReviewTools = false,
    formFooter = null,
    documentReviewIncludesPositions = true,
    documentReviewCreateExpense = false,
    showDocumentReviewCreateExpense = false,
    documentReviewKindLabel,
    documentReviewTags,
    onDocumentReviewCreateExpenseChange,
    showDocumentReviewBookingTarget = false,
    documentReviewBookingTarget,
    onDocumentReviewBookingTargetChange,
    documentReviewAllowDocumentTarget = true,
    documentReviewBookingTargetHint = null,
    documentReviewIsReReview = false,
    documentReviewPartyResolution = null,
    documentReviewDetails,
    previewOverride,
    documentReviewFormOnly = false,
    onSaveAbort,
    onSaveSuccess,
    onSaveError,
    onDmsReceiptUploaded,
  } = props
  const {
    id: parentId,
    receipts: initialReceipts,
    bookings,
    payee,
    data: payeeData,
    bookingContactName,
    type: payeeType,
    baseDataId,
    positions,
    economicAssetId = null,
    date,
    deliveryDate,
    readOnly,
    openPaymentDrawer,
  } = data
  const dateFor = deliveryDate || date
  const dispatch = useAppDispatch()
  // FLAG:useDocuAi — receipt uploads route through DMS→DocuAI when useDocuAi && dmsDirectUpload.
  const useDmsReceiptUpload = isDmsReceiptUploadEnabled()
  // FLAG:useDocuAi — the SAME upload the list dropzone runs. Its progress drives the spinner
  // below, so the spinner can only be showing while an upload is genuinely in flight.
  // FLAG:useDocuAi — T25: WHAT this form's upload is. With no saved receipt it IS the record
  // (create intent, complete-upload creates expense/revenue). With one open, the upload BELONGS
  // to it: intent 'uploadForReceipt' plus its id, and the server links the file when the upload
  // finishes — so leaving without saving no longer orphans the document. The form's dropzone and
  // the preview's upload button are mutually exclusive surfaces, so one decision covers both.
  const dmsUpload = useReceiptDmsUpload({
    type,
    action: data?.id ? RECEIPT_DMS_UPLOAD_ACTIONS.UPLOAD_FOR_RECEIPT : getReceiptCreateUploadAction(type),
    receiptId: data?.id ?? null,
  })
  const classes = useStyles()
  const filesToAssign = useAppSelector<FileToAssign[]>(
    state => state.receipt.edit.filesToAssign ?? [],
  )

  const subscriptions = useAppSelector(state => state.subscription.details.subscriptions)
  const [receipts, setReceipts] = useState(getMappedReceipts(initialReceipts))
  const [receiptsToDisplay, setReceiptsToDisplay] = useState([])
  const [receiptsToUpdate, setReceiptsToUpdate] = useState([])
  const [uploadedFilesIds, setUploadedFileIds] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const invalidDateFieldsRef = useRef([])
  const dateValidatorsRef = useRef({} as Record<string, () => boolean>)
  const latestDataRef = useRef(data)
  useEffect(() => { latestDataRef.current = data })
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [economicAssetModalOpen, setEconomicAssetModalOpen] = useState(false)
  const [receiptId, setReceiptId] = useState(null)
  const currentSkrType = useSelector(state => state.tenant.settings.currentSkrType)
  const isXInvoice = useMemo(() => {
    return receipts?.find((receipt) => receipt.validationResult)
  }, [receipts?.length])

  useEffect(() => {
    return () => {
      dispatch(clearFiles())
    }
  }, [])

  const hasPremiumDocumentOrMainSub = useMemo(() => {
    return (subscriptions || []).some(s =>
      !s.isTrial
      && (!s.end || moment(s.end).isAfter(moment()))
      && [SubscriptionTypeTypes.Document, SubscriptionTypeTypes.Main].includes(s.typeOfSubscription),
    )
  }, [subscriptions])

  const urls = useMemo(() => {
    return type === RECEIPT_TYPES.EXPENSE
      ? receiptConfig.expense
      : receiptConfig.revenue
  }, [type])
  // FLAG:useDocuAi — review mode is entered purely via the documentReviewFileId prop.
  const isDocumentInvoiceReviewMode = !!documentReviewFileId
  const useDocuAiEnabled = !!user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  // FLAG:useDocuAi — selection uses the same embedded review entry as a receipt upload.
  const selectedReview = useSelectedReceiptReview({
    useDocuAiEnabled,
    isNewReceipt: !parentId,
    readOnly: readOnly || isDocumentInvoiceReviewMode || documentReviewFormOnly,
    onReview: onDmsReceiptUploaded,
  })

  // FLAG:useDocuAi — without accounting there is nothing to book: neither line items nor payment
  // can be reviewed. „Nur Dokument“ is only the SAVE TARGET; with an accounting subscription the
  // extracted positions remain part of the document review and must stay visible. The rule itself
  // lives in shouldHideBookingSectionsInReview. `useCheckModuleAccess` answers whether the tenant
  // subscribed, while `useCheckAccessRights` answers whether this user may write expenses.
  const hasExpenseModule = useCheckModuleAccess(SUBSCRIPTION_MODULES.EXPENSES)
  const hasExpenseWriteRight = useCheckAccessRights(USER_RIGHTS.EXPENSES.WRITE)
  // Save guard only: selecting document must skip accounting creation without hiding review data.
  const documentOnlyReview = useDocuAiEnabled && isDocumentInvoiceReviewMode && documentReviewBookingTarget === 'document'
  const hideBookingSectionsWithoutAccounting = shouldHideBookingSectionsInReview({
    useDocuAiEnabled,
    isDocumentInvoiceReviewMode,
    hasExpenseModule,
    hasExpenseWriteRight,
  })

  useImperativeHandle(ref, () => ({
    onSave: () => {
      if (hasInvalidDateFields()) {
        toast.showToast({ type: 'error', message: lang.defaultErrorPleaseValidate })
        onSaveAbort()
        return
      }

      // FLAG:useDocuAi — review save skips the VAT-prompt modal and saves directly.
      if (isDocumentInvoiceReviewMode) {
        return startSaving()
      }

      try {
        const { doDisplay, allowSaving } = doDisplayModalBeforeSave(
          type === RECEIPT_TYPES.EXPENSE
            ? new ExpenseModel(props.data)
            : new RevenueModel(props.data),
        )

        // FLAG:useDocuAi — a VAT rate the tenant's own list does not contain must not become a dead
        // end. `doDisplayModalBeforeSave` (2023) compares the receipt's rates against
        // `user.getVatList()`, which `user_model.js:620` filters down to the TENANT's countries. An
        // Austrian 20 % is therefore simply unknown, `allowSaving` comes back false, and
        // vat-prompt-modal.component.js:28 then renders the dialog with „Nochmal prüfen" ALONE —
        // a question the user cannot answer, because the rate is stated on the invoice and the UI
        // locks it (receipt-form: lockCategoryUnbookableVat).
        //
        // That contradicted both the notice beside it — „Der Beleg kann geprüft und gespeichert
        // werden, eine Steuerkategorie lässt sich nicht zuordnen" (lang.js:6140) — and the review
        // path, which skips this dialog entirely a few lines above. The server agrees: both guards
        // return early for a position without a bookkeeping account
        // (validatePostionBookkeepingAccount.js:11, validateVatJurisdictionCategory.ts:88), and the
        // latter warns in its own header against exactly this dead end.
        //
        // The warning STAYS — it is the same question for every other rate mismatch — only the way
        // out is restored. Decision Ralf, 2026-09-16: the receipt must be saveable so a payment can
        // be assigned to it; it is simply not input-tax deductible.
        const unbookableVatFact = findUnbookableVatFact(useDocuAiEnabled, {
          invoiceFacts: props.data?.metaData?.invoiceFacts,
          reviewDetails: documentReviewDetails,
          relatedFiles: [...(props.data?.filesToAssign || []), ...(props.data?.receipts || [])],
        })

        if (!doDisplay) { return startSaving() }
        openVatPromptModal({
          allowSaving: allowSaving || unbookableVatFact?.bookable === false,
          onConfirm: () => { startSaving() },
          onAbort: () => { onSaveAbort() },
        })
      } catch {
        toast.showToast({ type: 'error', message: lang.defaultErrorPleaseValidate })
        onSaveAbort()
      }
    },
  }))

  const finishAnalyze = () => {
    setIsAnalyzing(false)
  }
  const onAnalyze = (fileId, autoAnalyze, skipModal = false, ocrData?) => {
    const data = { fileId, ocrData, props }
    const callbacks = {
      onStart: () => setIsAnalyzing(true),
      onSuccess: finishAnalyze,
      onApplyData: (ocrData) => onApplyData(ocrData),
      onResult: async() => {},
      onError: () => setIsAnalyzing(false),
    }

    const options = {
      autoAnalyze,
      skipModal: (skipModal || hasPremiumDocumentOrMainSub) && !ocrData,
    }
    DocumentAnalysisService.confirmAnalyze({ data, callbacks, options })
  }
  const checkIsAnalyzedBefore = (fileId, ocrData) => {
    onAnalyze(fileId, true, false, ocrData)
  }

  const onUploadComplete = (id, fileName, response) => {
    if (!response.success) return

    // if user uploads a zugferd or xml we create additional files which are added to the response
    const { data: files } = response

    const newFile = files.find((file) => file.name === fileName) ?? files[0]
    uploader.current.methods.setUuid(id, newFile.id)
    const localFile = uploader.current.methods.getFile(id)

    const reader = new FileReader()
    reader.addEventListener('load', () => {
      const filesToAssign = store.getState().receipt.edit.filesToAssign
      const assignedFiles = [
        ...files.filter(file => file.id !== newFile.id),
        { ...newFile, url: reader.result },
      ]

      dispatch(assignFiles(assignedFiles))

      // FLAG:useDocuAi — T23: the speed dial's upload goes through the legacy FineUploader, which
      // stamps no upload intent, so the server creates no record and the link would be made by the
      // SAVE alone. Leaving the form with the back arrow then left the document in DMS belonging to
      // nobody (measured on expense 12532 / file 24190, 2026-09-18). Link it now, for the receipt
      // that is actually open — a form that has never been saved has no id and keeps the old
      // behaviour, and so does every flag-OFF tenant.
      if (useDocuAiEnabled && data?.id) {
        assignedFiles.forEach((assignedFile) => {
          attachUploadedReceipt({ type, receiptId: data.id, fileId: assignedFile.id })
            .catch(() => { /* the save still links it; never block the upload on this */ })
        })
      }

      // prevent analyze if receipt already has files or is edited
      if (economicAssetId || (uploadedFilesIds?.length + filesToAssign?.length + receipts?.length) >= 1) { return }
      if (parentId) { return }
      // Upload above sets `scan: true`, so the DMS analysis already ran synchronously
      // as part of the upload request (see api uploadFile.js -> analyzeFile). This call
      // just fetches that already-finished result via the idempotent getOrCreateKeyValue -
      // it does not trigger a fresh DMS analysis, so it can't race the upload anymore.
      onAnalyze(newFile.id, true)
    }, false)
    reader.readAsDataURL(localFile)

    setUploadedFileIds([...uploadedFilesIds, ...files.map(file => file.id)])
    setIsUploading(false)
  }
  const onAllComplete = () => {
    if (!isSaving) { return }
    continueSave()
  }
  const onUploadError = (id, name, errorReason, xhr) => {
    // ignore the no files to upload error as it is not important
    if (errorReason !== NO_FILES_TO_UPLOAD_ERROR) {
      handleUploadError(xhr, errorReason, name)
    }
    setIsUploading(false)
  }
  const onValidateFileName = (file) => validateFileName(file, null, undefined, ALLOWED_FILE_EXTENSIONS, false)
  const onUpload = () => {
    setIsUploading(true)
  }

  // FLAG:useDocuAi — flag-ON receipt upload: send the file to DMS→DocuAI (no inline XML / OCR-fill),
  // then — mirroring the Documents flow — return to the expense/revenue list, where the analyzed
  // document surfaces as a "Zu prüfen" review row (getExpensesSSR union) and is checked in the shared
  // review component. Flag-OFF keeps the FineUploader path (uploader ref) below untouched.
  const onDmsReceiptUpload = async(batchItems) => {
    const localFiles = (batchItems || [])
      .map(item => item.file ?? item)
      .filter(Boolean)
    if (!localFiles.length) { return }

    // FLAG:useDocuAi — refuse unsupported formats and SAY SO (QA TC-1505). The legacy
    // FineUploader below validates extensions and shows lang.fileUploaderTypesError; this
    // DMS→DocuAI path had no check at all, so an unsupported file was handed to the upload and
    // the form reported nothing useful. Same helper as the expense/revenue dropzone, so both
    // entry points accept the same set and word the refusal the same way.
    const { accepted, rejected } = partitionReceiptUploadFiles(localFiles)
    const unsupportedMessage = getUnsupportedReceiptFilesMessage(rejected, lang.receipt.multiUpload.errors)
    if (unsupportedMessage) {
      toast.showToast({ type: 'error', message: unsupportedMessage })
    }
    if (!accepted.length) { return }

    // FLAG:useDocuAi — the SHARED hardened upload (use-receipt-dms-upload), the same one the
    // expense/revenue list dropzone runs: bounded batches, real per-file progress, accumulated
    // failures and a retry set. This used to be a second, unhardened implementation here.
    //
    // Crucially the spinner is now driven by the hook's own progress, which the upload's
    // resolution settles on EVERY path. Before, it was cleared by the host through
    // `onDmsReceiptUploaded` — and a host that did not pass that callback (the revenue form)
    // left it turning forever while the file had long since reached DMS and been analysed.
    const outcome = await dmsUpload.upload(accepted)

    if (outcome.failed > 0) {
      toast.showToast({ type: 'error', message: outcome.failureMessage ?? lang.defaultErrorMessage })
    }
    // FLAG:useDocuAi — T25: an upload INTO an open receipt is an attachment, not a new receipt.
    // It must behave exactly like the old FineUploader attachment did: appear in the preview
    // carousel and nothing else. No review surface, no analysis view — DMS/DocuAI analyse it
    // anyway, and the result reaches the record through the normal path. Reported 2026-09-18:
    // „wird es wie eine eigenständige ausgabe behandelt … soll genau wie vorher hochgeladen
    // werden und dann im carousel erscheinen".
    if (data?.id) {
      const infos = await Promise.all(outcome.fileIds.map(fileId => FileService.fetchFileInfo(fileId)
        .then(({ body }) => body)
        .catch(() => null)))
      const uploadedFiles = infos.filter(Boolean)
      if (uploadedFiles.length) { dispatch(assignFiles(uploadedFiles)) }
      return
    }
    // Hand the first created file up so the host can review it in place; the rest surface as
    // review rows in the matching list. No id at all means nothing was created — the failure
    // toast above already said so.
    const [firstFileId] = outcome.fileIds
    if (firstFileId) { onDmsReceiptUploaded?.(`${firstFileId}`) }
  }

  const onUploadErrorRef = useRef(onUploadError)
  const onUploadRef = useRef(onUpload)
  const onUploadCompleteRef = useRef(onUploadComplete)
  const onAllComponentRef = useRef(onAllComplete)
  const onValidateFileNameRef = useRef(onValidateFileName)
  onUploadErrorRef.current = onUploadError
  onUploadRef.current = onUpload
  onUploadCompleteRef.current = onUploadComplete
  onAllComponentRef.current = onAllComplete
  onValidateFileNameRef.current = onValidateFileName

  const uploader = useRef(new FineUploaderTraditional({
    options: {
      autoUpload: true,
      messages: {
        typeError: lang.fileUploaderTypesError.replace('%s', allowedExtensionsStrings),
        sizeError: lang.fileUploaderSizeError,
        maxHeightImageError: lang.fileUploaderMaxHeightImageError.replace('%s', MAX_IMAGE_HEIGHT.toString()),
        maxWidthImageError: lang.fileUploaderMaxWidthImageError.replace('%s', MAX_IMAGE_WIDTH.toString()),
        tooManyItemsError: lang.fileUploaderTooManyItemsError(MAX_FILES_PER_UPLOAD),
      },
      validation: {
        itemLimit: MAX_FILES_PER_UPLOAD,
        allowedExtensions: ALLOWED_FILE_EXTENSIONS,
        sizeLimit: MAX_FILESIZE_BYTES,
        image: {
          maxHeight: MAX_IMAGE_HEIGHT,
          maxWidth: MAX_IMAGE_WIDTH,
        },
      },
      request: {
        customHeaders: { authorization: `Bearer ${user.token}` },
        endpoint: documentConfig.uploadUrl,
        inputName: 'file',
        requireSuccessJson: false,
        params: {
          metaData: {
            path: type === RECEIPT_TYPES.EXPENSE ? SystemFolders.FinanceExpense : SystemFolders.FinanceRevenue,
          },
          transactionRelated: true,
          doAction: 'uploadForReceipt',
          // Triggers DMS analysis synchronously as part of this upload request (see onAnalyze
          // below), instead of racing it via a separate /analyze call after upload completes.
          scan: true,
        },
      },
      callbacks: {
        onError: onUploadErrorRef.current,
        onUpload: onUploadRef.current,
        onComplete: onUploadCompleteRef.current,
        onAllComplete: onAllComponentRef.current,
        onValidate: onValidateFileNameRef.current,
      },
    },
  }))

  useEffect(() => {
    setReceiptsToDisplay(receipts)
    if (filesToAssign.length) {
      filesToAssign.map((file) => dispatch(assignFile(file)))
      delete props.data.filesToAssign
    }
  }, [])

  const handelWebSocketMessage = (message) => {
    if (!isAnalyzing) { return }
    const { data: { id } } = message
    const receiptToAnalyze = [...receiptsToDisplay, ...receipts].find(receipt => receipt.fileId === id)
    const fileToAnalyze = filesToAssign.find(file => file.id === id)

    if (receiptToAnalyze || fileToAnalyze) {
      onAnalyze(id, true, true)
      // FLAG:useDocuAi — this page is one of the FILE:ANALYSED consumers, so it acks the terminal it
      // just acted on (see document/utils/ui-event-ack: every consumer must ack, or the delivery row
      // stays outstanding and is re-broadcast until the attempt cap). Only inside this branch: if
      // the event is for a file this page does not show, it processed nothing and must not ack.
      ackUiEvent(message?.data?.eventId)
    }
  }

  const onDataChanged = (newData) => {
    const mergedData = { ...data, ...newData, currentSkrType }
    latestDataRef.current = mergedData
    dispatch(setAndValidateData(mergedData))
  }
  // FLAG:useDocuAi — the ledger the buffered payments were entered under. „Nur Dokument" is not a
  // ledger, so it must not overwrite this: a reviewer who goes Ausgabe → Nur Dokument → Einnahme
  // still has to end up with revenue signs.
  const lastReviewLedgerRef = useRef<BookingTarget | null>(null)
  useEffect(() => {
    if (documentReviewBookingTarget === 'expense' || documentReviewBookingTarget === 'revenue') {
      lastReviewLedgerRef.current = documentReviewBookingTarget
    }
  }, [documentReviewBookingTarget])
  // FLAG:useDocuAi — an amount is stated from the RECEIPT's point of view (expense payment -1071,
  // the same payment on a revenue +1071). Moving the document to the other ledger therefore has to
  // move the payments the reviewer entered but has not saved yet — otherwise the review saved a
  // payment whose sign contradicted the chosen ledger. Only reachable in review mode: this handler
  // sits on the review header's BookingTargetRadio.
  const onReviewBookingTargetChange = (target: BookingTarget) => {
    if (isReviewLedgerFlip(lastReviewLedgerRef.current, target)) {
      const bufferedBookings = latestDataRef.current.bookings || []
      if (bufferedBookings.some(booking => booking?.isNew)) {
        onDataChanged({ bookings: flipReviewBookingAmounts(bufferedBookings) })
      }
    }
    if (target === 'expense' || target === 'revenue') {
      lastReviewLedgerRef.current = target
    }
    onDocumentReviewBookingTargetChange?.(target)
  }
  const onApplyData = async(ocrData) => {
    const newData = await prepareOcrFillData(ocrData, dateFor, false)

    if (!newData?.filledData?.length) { return }

    const { priceTotal, priceNet, priceGross, isGross } = newData

    // Prices follow the OCR data; the OPEN AMOUNT does not, once something has been paid on this
    // receipt — OCR reads a document and knows nothing about payments. See
    // resolve-applied-outstanding-amount.ts for the case that made this rule necessary.
    onDataChanged({
      ...newData,
      priceKind: isGross ? priceKinds.GROSS : priceKinds.NET,
      outstandingAmount: resolveAppliedOutstandingAmount(newData, data),
      priceTotal: priceTotal ?? priceGross ?? priceNet ?? data.priceTotal,
      ocrDataChanged: new Date().getTime(),
    })
  }
  const createBankTransfers = async(response) => {
    const { data: { id } } = response
    if (!bookings.length) return response

    const processIds = []
    const bankTransferResponses = []
    for (const booking of bookings) {
      const { bookingType, isNew } = booking

      // ignore already existing bookings and assignments as they are handled before
      if (!isNew || bookingType === RECEIPT_PAYMENT_TYPES.ASSIGNMENT) {
        continue
      }

      // only possible for expenses, not revenues
      if (bookingType === RECEIPT_PAYMENT_TYPES.BANK_TRANSFER && type === RECEIPT_TYPES.EXPENSE) {
        const processId = generateProcessId()
        const bankTransferData = ExpenseService.createBankTransferRequestData(booking)
        const response = await BookingService.bankTransfer.createByExpenseId(id, bankTransferData, processId)
        processIds.push(processId)
        bankTransferResponses.push(response)
        continue
      }

      const bookingUrl = type === RECEIPT_TYPES.EXPENSE
        ? paymentConfig.createAddPaymentForExpenseUrl(id)
        : paymentConfig.createAddPaymentForRevenueUrl(id)

      await request(bookingUrl, { method: 'POST', auth: true, data: booking })
    }

    const transactions = bankTransferResponses.map(({ body: { data } }) => data)
    await Promise.all(transactions.map((transaction, index) => pollTransaction(transaction, 'bookingApi', processIds[index])))

    if (type === RECEIPT_TYPES.EXPENSE) {
      await ExpenseService.syncState(id)
    }
  }
  const onCloseEconomicAssetModal = () => {
    setEconomicAssetModalOpen(false)
    handleSaveSuccess()
  }
  const onDateValidationChange = useCallback((isValid, name) => {
    const currentFields = invalidDateFieldsRef.current
    const nextFields = isValid
      ? currentFields.filter(field => field !== name)
      : currentFields.includes(name) ? currentFields : [...currentFields, name]

    invalidDateFieldsRef.current = nextFields
  }, [])
  const onRegisterDateValidation = useCallback((name, validate) => {
    if (!validate) {
      delete dateValidatorsRef.current[name]
      invalidDateFieldsRef.current = invalidDateFieldsRef.current.filter(field => field !== name)
      return
    }

    dateValidatorsRef.current[name] = validate
  }, [])
  const hasInvalidDateFields = () => {
    Object.values(dateValidatorsRef.current).forEach(validate => validate())
    return invalidDateFieldsRef.current.length > 0
  }
  const startSaving = async() => {
    if (isSaving) { return }

    setIsSaving(true)
    const uploads = uploader.current.methods.getUploads()
    const filesToUpload = uploads.filter(upload => upload.status === uploader.current.qq.status.SUBMITTED)
    if (filesToUpload.length > 0) {
      return uploader.current.methods.uploadStoredFiles()
    }
    await continueSave()
  }
  const savePayee = async() => {
    await Promise.all([
      updateReceipts(),
    ])
    if (payeeType === BASE_DATA_TYPES.SUPPLIER) {
      const supplierId = baseDataId
      if (supplierId) {
        const supplier = await SupplierService.getById(supplierId)
        const aliases = supplier?.aliases || []
        // remove null Values
        const sanitizedSupplier = omitBy(supplier, isNull)

        if (bookingContactName) {
          aliases.push(bookingContactName)
        }

        const updatedSupplier = await SupplierService.edit({ ...sanitizedSupplier, aliases })
        onDataChanged({ supplier: updatedSupplier })
        return { ...updatedSupplier, type: payeeType }
      } else {
        const { bankAccounts, addresses } = payeeData
        const [firstAddress] = addresses || []

        const newSupplierData = {
          name: payee,
          aliases: bookingContactName ? [bookingContactName] : [],
          addresses: [{
            ...firstAddress,
            isDefault: true,
            kind: 'company',
          }],
          bankAccounts,
        }
        const supplier = await SupplierService.create(newSupplierData)
        onDataChanged({ supplierId: supplier.id })
        return { ...supplier, type: payeeType }
      }
    } else {
      return { id: baseDataId, type: payeeType }
    }
  }
  const saveReceipt = async({ type: payeeType, ...customerOrSupplier }) => {
    const {
      id,
      paidThrough,
      bankAccountId,
      payDate,
      date,
      receiptNumber,
      tags,
      bookings = [],
      priceTotal,
      description,
      payee,
      ...otherProps
    } = latestDataRef.current
    const finalBookings = [...bookings]

    switch (paidThrough) {
      case CASH: {
        const bookingAmount = type === RECEIPT_TYPES.EXPENSE ? priceTotal * -1 : priceTotal
        finalBookings.push({
          id: undefined,
          type: CASH,
          bookingDate: payDate,
          isNew: true,
          bankAccountName: 'Kasse',
          bookingAmount,
          bookingType: RECEIPT_PAYMENT_TYPES.ASSIGNMENT,
          assignments: [
            // revenues somehow have also the expense assignmentType
            { assignmentType: 'expense', amount: bookingAmount },
          ],
        })
        break
      }
      case OFFLINE: {
        const bookingAmount = type === RECEIPT_TYPES.EXPENSE ? priceTotal * -1 : priceTotal
        finalBookings.push({
          id: undefined,
          type: OFFLINE,
          bookingDate: payDate,
          isNew: true,
          bankAccountId,
          bookingType: RECEIPT_PAYMENT_TYPES.ASSIGNMENT,
          bookingAmount,
          assignments: [
            // revenues somehow have also the expense assignmentType
            { assignmentType: 'expense', amount: bookingAmount },
          ],
        })
        break
      }
    }

    // build request data
    const hasNewFiles = !id && (uploadedFilesIds.length > 0 || filesToAssign.length > 0)
    const reqData: SaveRequest = {
      ...otherProps,
      priceTotal,
      description: description || '',
      receiptNumber: receiptNumber || '',
      date: date ? moment(date).format(config.dateFormat.api) : null,
      tags: tags || [],
      bookingAssignments: finalBookings
        .filter(booking => (booking.id || booking.bookingType === 'assignment'))
        .map(booking => (
          {
            ...booking,
            bookingDate: moment(moment(booking.bookingDate, config.dateFormat.client).format(config.dateFormat.api)).isValid()
              ? moment(booking.bookingDate, config.dateFormat.client).format(config.dateFormat.api)
              : booking.bookingDate,
          }),
        ), // remove dummy bookings
      fileIds: [...uploadedFilesIds, ...filesToAssign.map(file => ({ id: file.id, metaData: file.metaData }))],
      customerId: payeeType === BASE_DATA_TYPES.CUSTOMER ? customerOrSupplier.id : undefined,
      supplierId: payeeType === BASE_DATA_TYPES.CUSTOMER ? undefined : customerOrSupplier.id,
      isSeen: hasNewFiles ? false : undefined,
    }

    if (type === RECEIPT_TYPES.EXPENSE) {
      reqData.payee = payee
      return id
        ? ExpenseService.update(id, reqData)
        : ExpenseService.create(reqData)
    }

    reqData.sender = payee
    return id
      ? RevenueService.update(id, reqData)
      : RevenueService.create(reqData)
  }
  // FLAG:useDocuAi — invoice-review save path: persists the reviewed
  // documentInvoiceDetails via FileService.updateFileInfo; the backend gates the
  // optional expense creation on the tenant's accounting capabilities.
  const getDocumentReviewFile = () => {
    const reviewFiles = [
      ...filesToAssign,
      ...receiptsToDisplay,
      ...receipts,
    ]

    return reviewFiles.find(file => `${file?.fileId ?? file?.id}` === `${documentReviewFileId}`)
      || reviewFiles[0]
  }
  const buildDocumentInvoiceDetails = (reviewFile) => {
    const previousDetails = reviewFile?.documentInvoiceDetails || {}
    const ocr = reviewFile?.metaData?.ocr || {}
    const invoiceFacts = [data.invoiceFacts, data.metaData?.invoiceFacts, previousDetails.invoiceFacts]
      .find(isPlainObject)
    const source = data.metaData?.invoiceAnalysisSource
      || previousDetails.reviewSource?.source
      || ocr.invoiceAnalysisSource
      || ocr.type
    const reviewScope = documentReviewCreateExpense ? 'accounting' : 'document'
    const positionsReviewed = documentReviewCreateExpense || !!documentReviewIncludesPositions

    return {
      invoiceNumber: data.receiptNumber,
      // FLAG:useDocuAi — on a re-review the dialog's answer (correct / create new / keep link)
      // overrides what the form implied; null on every other save leaves the snapshot untouched.
      ...buildReviewPayeePersistence(data, previousDetails, documentReviewPartyResolution),
      // FLAG:useDocuAi — the IBAN must be CLEARABLE: an invalidly extracted IBAN makes validateBody
      // reject the ENTIRE review save with `documentInvoiceDetails.iban: NO_IBAN`, and clearing the
      // field is the only way out. The old `||` chain reversed that exact correction (`null` →
      // ingested IBAN → 400, while the field was EMPTY on screen). The rule and rationale — in
      // particular why `??` is NOT enough here — live
      // in resolveClearableReviewValue. This fixes all
      // three review hosts at once (Documents, expenses, revenues): they mount the same ReceiptEdit.
      // Since 2026-08-11, bic/bankInstitute follow the SAME rule: they are no longer display-only,
      // but separate review fields (issue bc3e6bbc). With the old `||` chain, an emptied BIC field
      // would have restored the ingested BIC on the next load — exactly the trap described above for
      // the IBAN.
      iban: resolveClearableReviewValue(data.iban, previousDetails.iban, ocr.payeeIban),
      bic: resolveClearableReviewValue(data.bic, previousDetails.bic, ocr.payeeBic),
      bankInstitute: resolveClearableReviewValue(
        data.bankInstitute, previousDetails.bankInstitute, ocr.payeeBank,
      ),
      // FLAG:useDocuAi — the reviewer-curated recognized-IBANs list (corrections + deletions)
      // goes back into the EXISTING payeeIbans column. `??`, not `||`: an emptied list is a
      // deliberate "none of these is the payee", not a missing value to fall back from.
      payeeIbans: data.payeeIbans ?? previousDetails.payeeIbans ?? null,
      // FLAG:useDocuAi — the ingested per-account bank block travels WITH that list so the
      // two stay index-aligned across a save: dropping a card must drop its account row,
      // or the reload prints that bank next to the following account. Same `??` rule —
      // an emptied list is the reviewer's decision, not a missing value.
      payeeAccounts: data.payeeAccounts ?? previousDetails.payeeAccounts ?? null,
      // FLAG:useDocuAi — the reviewed debit account goes back into the EXISTING debitIbans
      // column. `??` for the same reason: a cleared field is the reviewer saying "this
      // invoice states no debit account", not a missing value to fall back from.
      debitIbans: data.debitIbans ?? previousDetails.debitIbans ?? null,
      totalAmount: data.priceTotal,
      priceNet: data.priceNet,
      vatAmount: data.vatAmount,
      currency: data.currency,
      priceKind: data.priceKind,
      vat: data.vat,
      positions: data.positions,
      invoiceFacts,
      date: data.date,
      paymentDueDate: data.dueDate,
      payKindId: data.payKindId || previousDetails.payKindId || ocr.payKindId,
      payedAt: data.payDate,
      // FLAG:useDocuAi — the document's stated payment details are reviewer-editable fields (not
      // just a display banner); persist the reviewed values. build-review-data seeds them into
      // `data` from the ingested details, so `data` is authoritative — INCLUDING when the reviewer
      // clears a value. That was previously lost: `??` cannot distinguish „key absent" from
      // „set to — no entry provided —" (both are nullish), so save restored the ingested value and
      // „clear the paid status" did not survive the next load. The same rule as for the IBAN above;
      // an explicit 0 remains 0 (see isEmptyReviewValue).
      openAmount: resolveClearableReviewValue(data.openAmount, previousDetails.openAmount),
      paymentStatus: resolveClearableReviewValue(data.paymentStatus, previousDetails.paymentStatus),
      paymentMethodCode: resolveClearableReviewValue(data.paymentMethodCode, previousDetails.paymentMethodCode),
      paymentCardNumber: resolveClearableReviewValue(data.paymentCardNumber, previousDetails.paymentCardNumber),
      paymentCardScheme: resolveClearableReviewValue(data.paymentCardScheme, previousDetails.paymentCardScheme),
      // FLAG:useDocuAi — expense or revenue? The decision travels ONLY when this host offered it
      // (Documents review, no upload intent, accounting available). If the key is absent, the upsert
      // leaves the column alone — preserving an earlier decision rather than resetting it to
      // an expense from a host that never asks the question. This is exactly what leaves the other
      // review surfaces unchanged.
      ...(showDocumentReviewBookingTarget && documentReviewBookingTarget
        ? { bookingTarget: documentReviewBookingTarget }
        : {}),
      reviewStatus: positionsReviewed ? 'complete' : 'partial',
      reviewScope,
      reviewProvenance: 'userVerified',
      reviewSource: {
        source,
        documentType: data.metaData?.documentType || previousDetails.reviewSource?.documentType || ocr.documentType,
        invoiceValuesReadonly: !!(data.metaData?.invoiceValuesReadonly || previousDetails.reviewSource?.invoiceValuesReadonly || ocr.invoiceValuesReadonly),
      },
      requiresAccountingReview: !documentReviewCreateExpense,
      positionsReviewed,
    }
  }
  const saveDocumentInvoiceReview = async() => {
    const reviewFile = getDocumentReviewFile()
    const fileId = documentReviewFileId || reviewFile?.fileId || reviewFile?.id
    if (!fileId) {
      throw new Error('No document review file available')
    }

    const response = await saveDocumentInvoiceReviewRequest({
      updateFileInfo: FileService.updateFileInfo,
      fileId,
      // FLAG:useDocuAi — what the reviewer has in the form's tag field, not the snapshot the host
      // took of the file when the review was opened: editing a tag during the review used to be
      // discarded on Save. The save boundary adds the „Art" system tag back from `hostTags`
      // (mergeReviewTags), so the document keeps its classification whatever the reviewer does with
      // the other tags.
      tags: latestDataRef.current.tags,
      hostTags: documentReviewTags,
      documentInvoiceDetails: buildDocumentInvoiceDetails(reviewFile),
      createExpenseFromReview: !documentOnlyReview && documentReviewCreateExpense,
      // FLAG:useDocuAi — "review again": tell the server this save re-opens an already reviewed
      // invoice, so it re-derives the draft expense (the completing-save trigger fires only once).
      reReviewSave: documentReviewIsReReview,
      bookings: latestDataRef.current.bookings || [],
    })
    const bookingResult = response?.body?.meta?.reviewBookingResult
    if (useDocuAiEnabled && bookingResult?.booked === false && bookingResult.skipped
      && !['missingExpenseAccess', 'missingRevenueAccess', 'documentOnly'].includes(bookingResult.skipped)) {
      toast.showToast({
        type: 'info',
        message: lang.receiptArchive.notBooked,
      })
    }
  }

  const continueSave = async() => {
    try {
      // FLAG:useDocuAi — review mode persists the re-editable snapshot and explicitly asks the
      // server to create/update its tenant-scoped partner on Save. Merely editing the inline form
      // has no side effect. The legacy savePayee path remains below untouched.
      if (isDocumentInvoiceReviewMode) {
        if (!validateReviewPayeeForSave(data).isValid) {
          toast.showToast({ type: 'error', message: lang.defaultErrorPleaseValidate })
          setIsSaving(false)
          onSaveAbort?.()
          return
        }
        // FLAG:useDocuAi — an e-invoice judged INVALID is not saved casually. The KoSIT/format
        // verdict says this receipt must be rejected and must not be corrected — exactly why its
        // receipt values are locked here (XML_LOCKED_*). Saving MUST still be possible, or the
        // reviewer would lose everything they worked on alongside it (contact, tax categories,
        // payment details); it just has to be explicit.
        //
        // Here rather than in the host: `continueSave` is the ONE save point used by all three review
        // hosts (Documents page, drawer tab, receipt host) — wiring the same rule three times would
        // create exactly the drift the rest of this file avoids. And `data` already carries the
        // verdict: `build-review-data.ts` puts `validationResult` into the review record. Unreachable
        // with flag-OFF: only `isDocumentInvoiceReviewMode` enters this branch, and only flag-ON hosts
        // set the `documentReviewFileId` prop.
        //
        // NOT for "review again": the host has already shown ReReviewConfirmDialog there, which
        // contains the same warning (prop `invalidEInvoice`). In practice, two confirmations in a
        // row mean the second is dismissed without reading — so show exactly one: the one already in
        // front of the user.
        if (!documentReviewIsReReview && hasInvalidValidationResult(data)) {
          const confirmed = await awaitableConfirmationModal({
            isOpen: true,
            title: lang.documents.invalidEInvoiceSave.title,
            description: lang.documents.invalidEInvoiceSave.description,
            confirmLabel: lang.documents.invalidEInvoiceSave.confirm,
            cancelLabel: lang.documents.invalidEInvoiceSave.cancel,
          })
          if (!confirmed) {
            setIsSaving(false)
            onSaveAbort?.()
            return
          }
        }
        await saveDocumentInvoiceReview()
        handleSaveSuccess()
        return
      }

      const savedPayee = await savePayee()
      const receipt = await saveReceipt(savedPayee)
      await createBankTransfers(receipt)

      const { data: { id: receiptId } } = receipt
      if (isAllowedToOpenEconomicAssetModal && !economicAssetId && type === RECEIPT_TYPES.EXPENSE) {
        let positionsWithEconomicAssets
        if (accounts.length) {
          const accountNumbers = accounts.map(item => item.accountNo)
          positionsWithEconomicAssets = positions.filter(item => accountNumbers.includes(item.bookkeepingAccountNo))
        } else {
          positionsWithEconomicAssets = positions.filter(position => position.accountCategory === ACCOUNT_CATEGORY.investmentAccounts)
        }

        if (positionsWithEconomicAssets?.length && positionsWithEconomicAssets.length < 2) {
          setEconomicAssetModalOpen(true)
          setReceiptId(receiptId)
          return true
        }
      }
      handleSaveSuccess()
    } catch (error) {
      handleSaveError(error)
    }
  }

  const handleSaveSuccess = () => {
    setIsSaving(false)

    uploader.current.methods.clearStoredFiles()
    dispatch(clearFiles())
    setReceipts([])
    setReceiptsToUpdate([])
    setUploadedFileIds([])

    onSaveSuccess?.()
  }
  const handleSaveError = (error) => {
    const meta = error.body
      ? error.body.meta
      : null

    let message = lang.receipt.form.errors.defaultError(type)

    // FLAG:useDocuAi — changing the ledger was rejected because a receipt is already attached to the
    // document in the OTHER ledger (plan `review-booking-target` T8). The generic save-failed
    // toast would be the worst answer here: the reviewer has deliberately changed a
    // check box and needs to know WHY it cannot work. This comes BEFORE the field branches below
    // because the code lives on `expense` or `revenue` and conveys the same statement on either.
    const bookingTargetConflict = [meta?.expense?.[0]?.code, meta?.revenue?.[0]?.code]
      .includes(errorCodes.BOOKING_TARGET_CONFLICT)
    if (bookingTargetConflict) {
      toast.showToast({ type: 'error', message: lang.documents.reviewBookingTarget.conflictError })
      setIsSaving(false)
      onSaveError?.()
      return
    }

    // FLAG:useDocuAi — the shared review save writes the reviewed payment details through the
    // file endpoint. Its request validator reports invalid bank data on nested paths; without this
    // mapping every review host showed the unrelated generic expense/revenue save error. Keep the
    // branch inside the flag-ON document-review path so legacy receipt forms remain unchanged.
    if (useDocuAiEnabled && isDocumentInvoiceReviewMode) {
      if (meta?.['documentInvoiceDetails.iban']?.[0]?.code === errorCodes.NO_IBAN) {
        message = lang.documents.errors.invalidIban
      } else if (meta?.['documentInvoiceDetails.bic']?.[0]?.code === errorCodes.NO_BIC) {
        message = lang.documents.errors.invalidBic
      }
    }

    if (meta && meta.supplier) {
      switch (meta.supplier[0].code) {
        case NOT_FOUND: {
          message = lang.expenseSaveSupplierNotFoundError
          break
        }
      }
    } else if (meta && meta.positions) {
      switch (meta.positions[0].code) {
        case INVALID: {
          message = lang.expenseInvalidPositionsError
          break
        }
      }
    } else if (meta && meta.receiptNumber) {
      switch (meta.receiptNumber[0].code) {
        case MAX_LENGTH_EXCEEDED: {
          message = lang.receipt.form.errors.receiptNumberMaxLengthExceeded
          break
        }
      }

    } else if (meta && meta.length > 0) {
      if (meta[0].NEGATIVE_CASHBOOK_BALANCE) {
        message = lang.expenseSaveCashBalanceNegativeError
      } else if (meta[0].NOT_ALLOWED) {
        message = lang.expenseSavePaymentDatePastInitialBalanceDateError
      } else if (meta[0].EXPENSE_AMOUNT_EXCEEDED) {
        message = lang.expenseSavePaymentExpenseAmountExceeded
      } else if (meta[0].code === 'bankError') {
        message = meta[0].message
      } else if (meta[0].code === 'BankingAPIError') {
        message = meta[0].message
      }
    }

    toast.showToast({ type: 'error', message })
    setIsSaving(false)
    onSaveError?.()
  }
  const buildDeleteRequest = (id) => {
    const newReceipts = receipts.filter(receipt => id !== receipt.id)
    if (!newReceipts) { return }
    setReceipts(newReceipts)
    setReceiptsToDisplay([...newReceipts])

    return request(`${urls.receipt}/${id}`, {
      auth: true,
      method: 'DELETE',
      isConcurrent: true,
    })
  }

  const updateReceipts = () => {
    return receiptsToUpdate.map((receipt) => buildUpdateRequest(receipt.fileId, receipt.metaData))
  }

  const confirmDelete = (receipt) => {
    const {
      title,
      description,
      confirmButtonLabel,
      abortButtonLabel,
    } = lang.expense.navigationConfirmation

    modalConfirmation(
      () => buildDeleteRequest(receipt.id),
      { title, description, confirmButtonLabel, abortButtonLabel },
    )
  }

  const deleteFile = async(file) => {
    if (!uploadedFilesIds.includes(file.id)) { return }
    try {
      await request(`${documentConfig.resourceUrl}/${file.id}`, {
        auth: true,
        method: 'DELETE',
        isConcurrent: true,
      })
      const newUploadedFilesIds = uploadedFilesIds.filter(uploadedFileId => uploadedFileId !== file.id)
      setUploadedFileIds(newUploadedFilesIds)
    } catch {
      toast.showToast({ type: 'error', message: lang.expense.receipt.deleteTempFileError })
    }
  }
  const buildUpdateRequest = (id, metaData) => {
    return request(`${documentConfig.getUpdateUrl(id)}`, {
      auth: true,
      method: 'PUT',
      isConcurrent: true,
      data: { metaData },
    })
  }
  const onUpdateReceiptMetaData = (receipt, metaData) => {
    const { id, fileId } = receipt

    let linked = true
    const prevFile = filesToAssign.find(file => file.id === id)
    let fileToUpdate = prevFile ? { ...prevFile } : null
    if (!fileToUpdate) {
      fileToUpdate = receiptsToUpdate.find(receipt => receipt.id === id) || { id, data: {}, fileId }
      linked = false
    }

    if (!fileToUpdate) { return }
    fileToUpdate.metaData = { ...fileToUpdate.metaData, ...metaData }

    if (linked) {
      dispatch(assignFile(fileToUpdate))
    } else {
      const newState = receiptsToUpdate.filter(receipt => id !== receipt.id)
      newState.push(fileToUpdate)
      setReceiptsToUpdate(newState)
    }
  }

  useWebSocketEvent([EventTypes.FILE_ANALYZED], handelWebSocketMessage)

  return (
    <div className={classes.container}>
      <Loader customClass={classes.loader} visible={isSaving} text='Wird gespeichert...' />
      <div className={classes.content}>
        {/* FLAG:useDocuAi — the drawer-embedded review skips the whole upload/preview column
            (the drawer shows the preview itself); legacy callers never pass the prop. */}
        {!documentReviewFormOnly && <div className={classes.upload}>
          {/* FLAG:useDocuAi — the review page injects the invoice-artifact switcher here; legacy
              callers pass no override, so the normal receipt previewer renders unchanged. */}
          {previewOverride ?? (
            <>
              <div className={classes.analyze}>
                <Loader
                  customClass={classes.analyzeLoader}
                  visible={isAnalyzing || selectedReview.isPreparing}
                  text={lang.file.analyze.loaderText}
                  onCancel={selectedReview.isPreparing ? selectedReview.cancel : () => finishAnalyze()} />
                <span className={clsx(isAnalyzing && classes.analyzeAnimation)}></span>
              </div>
              {/* FLAG:useDocuAi — flag-ON the spinner follows the shared upload's own progress,
                  which its resolution always settles; flag-OFF it keeps the FineUploader's
                  isUploading flag verbatim. */}
              <Loader
                customClass={classes.analyzeLoader}
                visible={useDmsReceiptUpload ? dmsUpload.progress.todo > 0 : isUploading}
                text={lang.expense.receipt.uploading} />
              {/* FLAG:useDocuAi — the legacy „Dokument analysieren" entry point in the preview
                  toolbar is not wired at all with flag-ON. It calls the same `GET /file/:id/analyze`
                  that `api/src/services/fileService/analyzeFile.js` routes to the DocuAI pipeline
                  with flag-ON — so it starts a REAL new run, but here without confirmation
                  (skipModal as soon as no ocr data exists), without a named refusal when the tenant
                  quota is exhausted, and without double-click protection. The correct entry point
                  („Erneut analysieren", InvoiceReviewAnalysisActions) already sits in the save
                  split on these exact detail pages; with flag-ON there were therefore two adjacent
                  buttons, with the unprotected one more prominent. Without `onStartAnalyze`, the
                  toolbar does not render the button (receipt-previewer-item). Flag-OFF remains unchanged: the legacy
                  analysis IS correct there.   */}
              <ReceiptPreviewer
                type={type}
                receipts={receiptsToDisplay}
                uploader={uploader.current}
                useDmsUpload={useDmsReceiptUpload}
                onDmsUpload={onDmsReceiptUpload}
                onSelectDocument={selectedReview.onSelectDocument}
                onUpdateReceiptMetaData={onUpdateReceiptMetaData}
                accept={ALLOWED_FILE_EXTENSIONS}
                onDeleteReceipt={confirmDelete}
                onDeleteFile={deleteFile}
                onStartAnalyze={useDocuAiEnabled
                  ? undefined
                  : (fileId, ocrData) => checkIsAnalyzedBefore(fileId, ocrData)}
                dateFor={dateFor}
                hideDownload={!parentId}
                readOnly={readOnly || isDocumentInvoiceReviewMode}
                disableOcr={data.economicAssetId || isXInvoice}
              />
            </>
          )}
        </div>}
        <div className={clsx(classes.form, { [classes.formOnly]: documentReviewFormOnly })}>
          {/* FLAG:useDocuAi — the document's stated payment values (editable, persist on
              save) render inside ReceiptForm's payment card (see ReceiptForm →
              documentReviewDetails/isDocumentInvoiceReviewMode), so all payment info sits
              together. Every entry point shows and saves them identically. */}
          {/* FLAG:useDocuAi — invoice-review header (kind label + optional "Ausgabe erstellen"). */}
          {isDocumentInvoiceReviewMode
            && (documentReviewKindLabel || showDocumentReviewCreateExpense || showDocumentReviewBookingTarget) && (
            <div className={classes.reviewActions}>
              {documentReviewKindLabel && (
                <div className={classes.reviewKind}>
                  <span className={classes.reviewKindCaption}>{lang.documents.reviewInvoiceKindLabel || lang.tag.kindLabel}</span>
                  <span className={classes.reviewKindValue}>{documentReviewKindLabel}</span>
                </div>
              )}
              {/* FLAG:useDocuAi — the booking decision belongs in the review header, not in the
                  contact section: it says which LEDGER is affected, not who the counterparty
                  is. The third choice saves the document without creating an accounting receipt.           */}
              {useDocuAiEnabled && showDocumentReviewBookingTarget && (
                <>
                  <BookingTargetRadio
                    value={documentReviewBookingTarget || 'expense'}
                    onChange={onReviewBookingTargetChange}
                    allowDocumentTarget={documentReviewAllowDocumentTarget}
                  />
                  {documentReviewBookingTargetHint && (
                    <div className={classes.reviewActionHint} data-qs-id='receiptReview-bookingTargetHint'>
                      {documentReviewBookingTargetHint}
                    </div>
                  )}
                </>
              )}
              {showDocumentReviewCreateExpense && (
                <Checkbox
                  name='createExpenseFromReview'
                  dataQsId='receiptReview-createExpenseFromReview'
                  checked={documentReviewCreateExpense}
                  onChange={onDocumentReviewCreateExpenseChange}
                  useFormik={false}
                >
                  <span className={classes.reviewActionLabel}>{lang.booking.createExpenseOrRevenue.expenseTitle}</span>
                </Checkbox>
              )}
            </div>
          )}
          <ReceiptEditFormWrapper
            type={type}
            data={data}
            bookings={bookings}
            readOnly={readOnly}
            bookingFieldsReadOnly={bookingFieldsReadOnly}
            isSaving={isSaving}
            openPaymentDrawer={openPaymentDrawer}
            isAnalysingMode={isAnalysingMode}
            onDateValidationChange={onDateValidationChange}
            onRegisterDateValidation={onRegisterDateValidation}
            onChange={onDataChanged}
            // FLAG:useDocuAi — render the "Zahlungsangaben" fields inside
            // ReceiptForm's payment card during invoice review.
            isDocumentInvoiceReviewMode={isDocumentInvoiceReviewMode}
            // FLAG:useDocuAi — the review host's manual branch passes no documentReviewFileId, so
            // the wrapper needs this second marker to keep the legacy analysis card out (T22).
            documentReviewFormOnly={documentReviewFormOnly}
            positionReviewTools={positionReviewTools}
            // FLAG:useDocuAi — the reviewed document's identity must reach the line-item list: it
            // keys the list's „Zurücksetzen" snapshot. The review data (buildReviewData) has no
            // `data.id`, which previously served as the key.
            documentReviewFileId={documentReviewFileId}
            documentReviewDetails={documentReviewDetails}
            hideBookingSectionsWithoutAccounting={hideBookingSectionsWithoutAccounting}
          />
          {/* FLAG:docuAiEnhancements — additive slot at the END of the form column; see the prop. */}
          {formFooter}
        </div>
        {economicAssetModalOpen && type === RECEIPT_TYPES.EXPENSE && (
          <CreateEconomicAssetModal
            onClose={onCloseEconomicAssetModal}
            open={economicAssetModalOpen}
            expenseId={receiptId}
          />
        )}
      </div>
    </div>
  )
})

export default ReceiptEdit
